// ============================================================================
// GERENCIAMENTO DE AÇÕES SOCIAIS - DASHBOARD ADMIN
// ============================================================================
// Arquivo: src/components/Dashboard_ADM/GerenciamentoAcoesSociais.tsx
// CREATED: 2026-07-20 — mesmo padrão visual do GerenciamentoNoticiasArtigos
// ============================================================================

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Search, Plus, Trash2, Eye, Calendar, Edit, Upload, CheckCircle,
  Clock, XCircle, Loader2, AlertTriangle, Send, X, Archive,
  Heart, MapPin, Users, Link as LinkIcon, Globe, Image as ImageIcon,
} from 'lucide-react';
import { API_BASE, authH } from '../../utils/apiConfig';

// ─── Types ───────────────────────────────────────────────────────────────────
interface Partner {
  name:     string;
  logoUrl?: string;
  website?: string;
}

interface SocialAction {
  id:           string;
  title:        string;
  slug:         string;
  description:  string;
  summary:      string | null;
  coverImageUrl: string | null;
  eventDate:    string | null;
  location:     string | null;
  partners:     Partner[];
  status:       'draft' | 'published' | 'archived';
  publishedAt:  string | null;
  viewsCount:   number;
  createdAt:    string;
}

interface ActionStats {
  total: number; published: number; draft: number; archived: number;
}

interface FormState {
  title:        string;
  description:  string;
  summary:      string;
  coverImageUrl: string;
  eventDate:    string;
  location:     string;
  partners:     Partner[];
}

const EMPTY_FORM: FormState = {
  title: '', description: '', summary: '', coverImageUrl: '',
  eventDate: '', location: '', partners: [],
};

// ─── Partner Editor ───────────────────────────────────────────────────────────
const PartnersEditor: React.FC<{
  partners: Partner[];
  onChange: (p: Partner[]) => void;
}> = ({ partners, onChange }) => {
  const [newPartner, setNewPartner] = useState<Partner>({ name: '', logoUrl: '', website: '' });

  const add = () => {
    if (!newPartner.name.trim()) return;
    onChange([...partners, {
      name:    newPartner.name.trim(),
      logoUrl: newPartner.logoUrl?.trim() || undefined,
      website: newPartner.website?.trim() || undefined,
    }]);
    setNewPartner({ name: '', logoUrl: '', website: '' });
  };

  const remove = (idx: number) => onChange(partners.filter((_, i) => i !== idx));

  return (
    <div className="space-y-3">
      {/* Lista de parceiros adicionados */}
      {partners.length > 0 && (
        <div className="space-y-2">
          {partners.map((p, idx) => (
            <div key={idx} className="flex items-center gap-3 bg-gray-50 rounded-xl px-4 py-2.5 border border-gray-200">
              {p.logoUrl && (
                <img src={p.logoUrl} alt={p.name} className="w-8 h-8 object-contain rounded" />
              )}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-bold text-gray-900 truncate">{p.name}</p>
                {p.website && (
                  <a href={p.website} target="_blank" rel="noopener noreferrer"
                    className="text-xs text-blue-500 hover:underline truncate block">{p.website}</a>
                )}
              </div>
              <button type="button" onClick={() => remove(idx)}
                className="w-7 h-7 rounded-lg bg-red-100 text-red-600 flex items-center justify-center hover:bg-red-200 flex-shrink-0">
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Formulário para novo parceiro */}
      <div className="border-2 border-dashed border-gray-300 rounded-xl p-4 space-y-3 bg-gray-50">
        <p className="text-xs font-bold text-gray-500 uppercase tracking-wider">Adicionar parceiro/patrocinador</p>
        <input
          value={newPartner.name}
          onChange={e => setNewPartner(p => ({ ...p, name: e.target.value }))}
          placeholder="Nome do parceiro *"
          className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg text-sm text-gray-900 focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
        />
        <input
          value={newPartner.logoUrl}
          onChange={e => setNewPartner(p => ({ ...p, logoUrl: e.target.value }))}
          placeholder="URL do logo (opcional)"
          className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg text-sm text-gray-900 focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
          type="url"
        />
        <input
          value={newPartner.website}
          onChange={e => setNewPartner(p => ({ ...p, website: e.target.value }))}
          placeholder="Site do parceiro (opcional)"
          className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg text-sm text-gray-900 focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
          type="url"
        />
        <button type="button" onClick={add} disabled={!newPartner.name.trim()}
          className="w-full py-2 bg-emerald-600 text-white rounded-lg font-bold text-sm hover:bg-emerald-700 transition-all flex items-center justify-center gap-2 disabled:opacity-40">
          <Plus className="w-4 h-4" /> Adicionar Parceiro
        </button>
      </div>

      {partners.length === 0 && (
        <p className="text-xs text-gray-400 text-center">Nenhum parceiro adicionado ainda</p>
      )}
    </div>
  );
};

// ─── Status Badge ────────────────────────────────────────────────────────────
const StatusBadge: React.FC<{ status: SocialAction['status'] }> = ({ status }) => {
  const cfg = {
    published: { label: 'Publicada', bg: 'bg-emerald-100', text: 'text-emerald-800', icon: CheckCircle },
    draft:     { label: 'Rascunho',  bg: 'bg-gray-100',    text: 'text-gray-700',    icon: Clock },
    archived:  { label: 'Arquivada', bg: 'bg-red-100',     text: 'text-red-700',     icon: XCircle },
  }[status] ?? { label: status, bg: 'bg-gray-100', text: 'text-gray-700', icon: Clock };
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold ${cfg.bg} ${cfg.text}`}>
      <Icon className="w-3.5 h-3.5" /> {cfg.label}
    </span>
  );
};

// ─── Modal de Formulário ──────────────────────────────────────────────────────
const FormModal: React.FC<{
  title:     string;
  form:      FormState;
  onChange:  (f: FormState) => void;
  onSave:    () => void;
  onClose:   () => void;
  saving:    boolean;
  saveLabel: string;
}> = ({ title, form, onChange, onSave, onClose, saving, saveLabel }) => {
  const fileRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);

  const set = (key: keyof FormState, val: any) =>
    onChange({ ...form, [key]: val });

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { alert('Máximo 5MB'); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('folder', 'social-actions');
      const auth = authH() as Record<string, string>;
      const res = await fetch(`${API_BASE}/upload`, {
        method: 'POST',
        headers: { Authorization: auth['Authorization'] },
        body: fd,
      });
      if (!res.ok) throw new Error('Erro no upload');
      const data = await res.json();
      set('coverImageUrl', data.url);
    } catch (err: any) { alert(err.message ?? 'Erro ao fazer upload'); }
    finally { setUploading(false); }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-auto shadow-2xl">
        {/* Header */}
        <div className="p-6 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white z-10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center">
              <Heart className="w-5 h-5 text-emerald-600" />
            </div>
            <h3 className="text-xl font-black text-gray-900">{title}</h3>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6 space-y-5">
          {/* Imagem de Capa */}
          <div>
            <label className="block text-sm font-bold text-gray-700 mb-2">Imagem de Capa</label>
            <div className="border-2 border-dashed border-gray-300 rounded-xl p-4 text-center hover:border-emerald-400 transition-all">
              {form.coverImageUrl ? (
                <div className="relative">
                  <img src={form.coverImageUrl} alt="Preview" className="w-full h-48 object-cover rounded-lg" />
                  <button onClick={() => set('coverImageUrl', '')}
                    className="absolute top-2 right-2 p-1.5 bg-red-600 text-white rounded-lg hover:bg-red-700">
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <div className="space-y-3">
                  <input ref={fileRef} type="file" accept="image/*" onChange={handleImageUpload} className="hidden" />
                  <button onClick={() => fileRef.current?.click()} disabled={uploading}
                    className="px-5 py-2.5 bg-emerald-600 text-white font-bold rounded-xl hover:bg-emerald-700 flex items-center gap-2 mx-auto disabled:opacity-50">
                    {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                    {uploading ? 'Enviando...' : 'Upload da Capa'}
                  </button>
                  <p className="text-xs text-gray-400">ou</p>
                  <input type="url" placeholder="https://exemplo.com/imagem.jpg"
                    value={form.coverImageUrl}
                    onChange={e => set('coverImageUrl', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                </div>
              )}
            </div>
          </div>

          {/* Título */}
          <div>
            <label className="block text-sm font-bold text-gray-700 mb-2">Título *</label>
            <input value={form.title} onChange={e => set('title', e.target.value)}
              placeholder="Nome da ação social" maxLength={200}
              className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl text-gray-900 font-semibold focus:ring-2 focus:ring-emerald-500 focus:border-transparent" />
          </div>

          {/* Resumo */}
          <div>
            <label className="block text-sm font-bold text-gray-700 mb-2">Resumo</label>
            <textarea value={form.summary} onChange={e => set('summary', e.target.value)}
              rows={2} maxLength={500}
              placeholder="Breve descrição que aparecerá nos cards (max 500 caracteres)"
              className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl text-gray-900 resize-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent" />
          </div>

          {/* Descrição Completa */}
          <div>
            <label className="block text-sm font-bold text-gray-700 mb-2">Descrição Completa *</label>
            <textarea value={form.description} onChange={e => set('description', e.target.value)}
              rows={8}
              placeholder="Descreva a ação social em detalhes (suporta HTML básico)"
              className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl text-gray-900 resize-none font-mono text-sm focus:ring-2 focus:ring-emerald-500 focus:border-transparent" />
          </div>

          {/* Data e Local */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2 flex items-center gap-1.5">
                <Calendar className="w-4 h-4 text-emerald-600" /> Data de Realização
              </label>
              <input type="date" value={form.eventDate} onChange={e => set('eventDate', e.target.value)}
                className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl text-gray-900 focus:ring-2 focus:ring-emerald-500 focus:border-transparent" />
            </div>
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2 flex items-center gap-1.5">
                <MapPin className="w-4 h-4 text-emerald-600" /> Localização
              </label>
              <input value={form.location} onChange={e => set('location', e.target.value)}
                placeholder="Ex: Bairro X, São Luís — MA"
                className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl text-gray-900 focus:ring-2 focus:ring-emerald-500 focus:border-transparent" />
            </div>
          </div>

          {/* Parceiros */}
          <div>
            <label className="block text-sm font-bold text-gray-700 mb-2 flex items-center gap-1.5">
              <Users className="w-4 h-4 text-emerald-600" /> Parceiros / Patrocinadores
            </label>
            <PartnersEditor partners={form.partners} onChange={p => set('partners', p)} />
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-200 flex gap-3 sticky bottom-0 bg-white">
          <button onClick={onClose}
            className="flex-1 px-4 py-3 rounded-xl border-2 border-gray-300 text-gray-700 font-bold hover:bg-gray-50">
            Cancelar
          </button>
          <button onClick={onSave}
            disabled={saving || !form.title.trim() || !form.description.trim()}
            className="flex-1 px-4 py-3 rounded-xl bg-emerald-600 text-white font-bold hover:bg-emerald-700 flex items-center justify-center gap-2 disabled:opacity-60 transition-all">
            {saving ? <><Loader2 className="w-5 h-5 animate-spin" /> Salvando...</> : <><Send className="w-5 h-5" /> {saveLabel}</>}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Componente Principal ─────────────────────────────────────────────────────
const GerenciamentoAcoesSociais: React.FC = () => {
  const [actions, setActions]         = useState<SocialAction[]>([]);
  const [stats, setStats]             = useState<ActionStats | null>(null);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState<string | null>(null);
  const [busca, setBusca]             = useState('');
  const [filtroStatus, setFiltroStatus] = useState('todos');
  const [salvando, setSalvando]       = useState(false);

  const [modalCriar, setModalCriar]   = useState(false);
  const [formCriar, setFormCriar]     = useState<FormState>(EMPTY_FORM);

  const [modalEditar, setModalEditar] = useState(false);
  const [formEditar, setFormEditar]   = useState<FormState>(EMPTY_FORM);
  const [editandoId, setEditandoId]   = useState<string | null>(null);

  const [detalhes, setDetalhes]       = useState<SocialAction | null>(null);

  // ── Fetch ────────────────────────────────────────────────────────────────
  const fetchActions = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const params = new URLSearchParams({ page: '1', limit: '50' });
      if (filtroStatus !== 'todos') params.set('status', filtroStatus);
      const res = await fetch(`${API_BASE}/social-actions?${params}`, { headers: authH() });
      if (!res.ok) throw new Error(`Erro ${res.status}`);
      const data = await res.json();
      setActions(data.actions ?? []);
    } catch (e: any) { setError(e.message); }
    finally { setLoading(false); }
  }, [filtroStatus]);

  const fetchStats = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/social-actions/stats/summary`, { headers: authH() });
      if (!res.ok) return;
      setStats(await res.json());
    } catch { /* opcional */ }
  }, []);

  useEffect(() => { fetchActions(); }, [fetchActions]);
  useEffect(() => { fetchStats(); },  [fetchStats]);

  // ── CRUD ─────────────────────────────────────────────────────────────────
  const handleCriar = async () => {
    setSalvando(true);
    try {
      // 1. Cria
      const resCreate = await fetch(`${API_BASE}/social-actions`, {
        method: 'POST',
        headers: { ...authH(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title:         formCriar.title,
          description:   formCriar.description,
          summary:       formCriar.summary       || undefined,
          coverImageUrl: formCriar.coverImageUrl || undefined,
          eventDate:     formCriar.eventDate     || undefined,
          location:      formCriar.location      || undefined,
          partners:      formCriar.partners,
        }),
      });
      if (!resCreate.ok) {
        const err = await resCreate.json().catch(() => ({}));
        throw new Error(Array.isArray(err.message) ? err.message.join(', ') : err.message ?? 'Erro ao criar');
      }
      const created = await resCreate.json();

      // 2. Publica imediatamente
      await fetch(`${API_BASE}/social-actions/${created.id}/publish`, {
        method: 'POST', headers: authH(),
      });

      alert('Ação social publicada com sucesso!');
      setModalCriar(false);
      setFormCriar(EMPTY_FORM);
      fetchActions(); fetchStats();
    } catch (err: any) { alert(err.message ?? 'Erro ao criar'); }
    finally { setSalvando(false); }
  };

  const handleEditar = async () => {
    if (!editandoId) return;
    setSalvando(true);
    try {
      const res = await fetch(`${API_BASE}/social-actions/${editandoId}`, {
        method: 'PATCH',
        headers: { ...authH(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title:         formEditar.title         || undefined,
          description:   formEditar.description   || undefined,
          summary:       formEditar.summary       || undefined,
          coverImageUrl: formEditar.coverImageUrl || undefined,
          eventDate:     formEditar.eventDate     || undefined,
          location:      formEditar.location      || undefined,
          partners:      formEditar.partners,
        }),
      });
      if (!res.ok) throw new Error('Erro ao salvar');
      alert('Ação social atualizada!');
      setModalEditar(false);
      setEditandoId(null);
      fetchActions();
    } catch (err: any) { alert(err.message ?? 'Erro ao salvar'); }
    finally { setSalvando(false); }
  };

  const handlePublicar = async (id: string) => {
    if (!confirm('Publicar esta ação social?')) return;
    setSalvando(true);
    try {
      const res = await fetch(`${API_BASE}/social-actions/${id}/publish`, {
        method: 'POST', headers: authH(),
      });
      if (!res.ok) throw new Error('Erro ao publicar');
      alert('Publicada!');
      fetchActions(); fetchStats(); setDetalhes(null);
    } catch (err: any) { alert(err.message); }
    finally { setSalvando(false); }
  };

  const handleArquivar = async (id: string) => {
    if (!confirm('Arquivar esta ação social?')) return;
    setSalvando(true);
    try {
      const res = await fetch(`${API_BASE}/social-actions/${id}/archive`, {
        method: 'POST', headers: authH(),
      });
      if (!res.ok) throw new Error('Erro ao arquivar');
      alert('Arquivada.');
      fetchActions(); fetchStats(); setDetalhes(null);
    } catch (err: any) { alert(err.message); }
    finally { setSalvando(false); }
  };

  const handleDeletar = async (id: string, titulo: string) => {
    if (!confirm(`Deletar permanentemente "${titulo}"?`)) return;
    setSalvando(true);
    try {
      const res = await fetch(`${API_BASE}/social-actions/${id}`, {
        method: 'DELETE', headers: authH(),
      });
      if (!res.ok) throw new Error('Erro ao deletar');
      alert('Deletada!');
      setDetalhes(null);
      fetchActions(); fetchStats();
    } catch (err: any) { alert(err.message); }
    finally { setSalvando(false); }
  };

  const abrirEditar = (action: SocialAction) => {
    setEditandoId(action.id);
    setFormEditar({
      title:         action.title,
      description:   action.description,
      summary:       action.summary ?? '',
      coverImageUrl: action.coverImageUrl ?? '',
      eventDate:     action.eventDate ?? '',
      location:      action.location ?? '',
      partners:      action.partners ?? [],
    });
    setModalEditar(true);
  };

  // ── Filtro client-side por busca ─────────────────────────────────────────
  const actionsFiltradas = actions.filter(a =>
    !busca.trim() || a.title.toLowerCase().includes(busca.toLowerCase()),
  );

  const fmtDate = (d?: string | null) => {
    if (!d) return '—';
    const part = String(d).slice(0, 10);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(part)) return d;
    const [y, m, day] = part.split('-');
    return `${day}/${m}/${y}`;
  };

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <div className="p-8 bg-gradient-to-br from-gray-50 to-emerald-50 min-h-screen">
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-12 h-12 rounded-2xl bg-emerald-100 flex items-center justify-center">
            <Heart className="w-6 h-6 text-emerald-600" />
          </div>
          <div>
            <h1 className="text-4xl font-black text-gray-900">Ações Sociais</h1>
            <p className="text-gray-600">Gerencie e publique iniciativas sociais da Federação</p>
          </div>
        </div>
      </div>

      {/* STATS */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {[
            { label: 'Total',      value: stats.total,     color: 'emerald', icon: Heart },
            { label: 'Publicadas', value: stats.published, color: 'green',   icon: CheckCircle },
            { label: 'Rascunhos',  value: stats.draft,     color: 'gray',    icon: Clock },
            { label: 'Arquivadas', value: stats.archived,  color: 'red',     icon: XCircle },
          ].map((s, i) => {
            const Icon = s.icon;
            return (
              <div key={i} className="bg-white rounded-2xl border-2 border-gray-200 p-5 hover:shadow-lg transition-all">
                <div className={`w-10 h-10 rounded-xl bg-${s.color}-100 flex items-center justify-center mb-3`}>
                  <Icon className={`w-5 h-5 text-${s.color}-600`} />
                </div>
                <p className="text-3xl font-black text-gray-900">{s.value}</p>
                <p className="text-sm font-semibold text-gray-600">{s.label}</p>
              </div>
            );
          })}
        </div>
      )}

      {/* FILTROS + BOTÃO CRIAR */}
      <div className="bg-white rounded-2xl border-2 border-gray-200 p-6 mb-8">
        <div className="flex flex-wrap gap-4">
          <div className="flex-1 min-w-[240px] relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input value={busca} onChange={e => setBusca(e.target.value)}
              placeholder="Buscar ações sociais..."
              className="w-full pl-12 pr-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent" />
          </div>
          <select value={filtroStatus} onChange={e => setFiltroStatus(e.target.value)}
            className="px-4 py-3 border-2 border-gray-300 rounded-xl font-semibold text-gray-700">
            <option value="todos">Todos os Status</option>
            <option value="published">Publicadas</option>
            <option value="draft">Rascunhos</option>
            <option value="archived">Arquivadas</option>
          </select>
          <button onClick={() => { setFormCriar(EMPTY_FORM); setModalCriar(true); }}
            className="px-6 py-3 bg-emerald-600 text-white font-bold rounded-xl hover:bg-emerald-700 transition-all flex items-center gap-2">
            <Plus className="w-5 h-5" /> Nova Ação Social
          </button>
        </div>
      </div>

      {/* Loading / Error */}
      {loading && (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-12 h-12 text-emerald-600 animate-spin" />
        </div>
      )}
      {error && (
        <div className="bg-red-50 border-2 border-red-200 rounded-2xl p-6 flex items-center gap-4">
          <AlertTriangle className="w-8 h-8 text-red-600 flex-shrink-0" />
          <div><p className="font-bold text-red-900">Erro ao carregar</p><p className="text-red-700">{error}</p></div>
        </div>
      )}

      {/* GRID */}
      {!loading && !error && actionsFiltradas.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {actionsFiltradas.map(action => (
            <div key={action.id}
              className="bg-white rounded-2xl border-2 border-gray-200 overflow-hidden hover:shadow-xl transition-all flex flex-col">
              {/* Capa */}
              <div className="h-44 bg-gradient-to-br from-emerald-100 to-emerald-200 flex items-center justify-center overflow-hidden flex-shrink-0 relative">
                {action.coverImageUrl
                  ? <img src={action.coverImageUrl} alt={action.title} className="w-full h-full object-cover" />
                  : <Heart className="w-14 h-14 text-emerald-400" />}
                <div className="absolute top-3 left-3">
                  <StatusBadge status={action.status} />
                </div>
              </div>

              <div className="p-5 flex flex-col flex-1">
                <h3 className="text-lg font-black text-gray-900 mb-2 line-clamp-2">{action.title}</h3>
                <p className="text-sm text-gray-600 mb-3 line-clamp-2 flex-1">{action.summary ?? action.description}</p>

                {/* Metadados */}
                <div className="space-y-1.5 mb-4 pb-4 border-b border-gray-100 text-xs text-gray-500">
                  {action.eventDate && (
                    <div className="flex items-center gap-2">
                      <Calendar className="w-3.5 h-3.5 text-emerald-500" />
                      <span>{fmtDate(action.eventDate)}</span>
                    </div>
                  )}
                  {action.location && (
                    <div className="flex items-center gap-2">
                      <MapPin className="w-3.5 h-3.5 text-emerald-500" />
                      <span className="truncate">{action.location}</span>
                    </div>
                  )}
                  {action.partners.length > 0 && (
                    <div className="flex items-center gap-2">
                      <Users className="w-3.5 h-3.5 text-emerald-500" />
                      <span>{action.partners.length} parceiro{action.partners.length > 1 ? 's' : ''}</span>
                    </div>
                  )}
                  <div className="flex items-center gap-2">
                    <Eye className="w-3.5 h-3.5" />
                    <span>{action.viewsCount} visualizações</span>
                  </div>
                </div>

                {/* Ações */}
                <div className="flex gap-2 flex-wrap">
                  <button onClick={() => setDetalhes(action)}
                    className="flex-1 min-w-0 px-3 py-2 rounded-lg bg-emerald-600 text-white font-bold hover:bg-emerald-700 transition-all flex items-center justify-center gap-1.5 text-sm">
                    <Eye className="w-4 h-4" /> Ver
                  </button>
                  <button onClick={() => abrirEditar(action)}
                    className="px-3 py-2 rounded-lg border-2 border-amber-300 text-amber-600 hover:bg-amber-50 transition-all text-sm font-bold"
                    title="Editar">
                    <Edit className="w-4 h-4" />
                  </button>
                  {action.status !== 'published' && (
                    <button onClick={() => handlePublicar(action.id)} disabled={salvando}
                      className="px-3 py-2 rounded-lg bg-green-600 text-white hover:bg-green-700 transition-all disabled:opacity-50 text-sm font-bold"
                      title="Publicar">
                      <Send className="w-4 h-4" />
                    </button>
                  )}
                  {action.status === 'published' && (
                    <button onClick={() => handleArquivar(action.id)} disabled={salvando}
                      className="px-3 py-2 rounded-lg bg-amber-500 text-white hover:bg-amber-600 transition-all disabled:opacity-50 text-sm font-bold"
                      title="Arquivar">
                      <Archive className="w-4 h-4" />
                    </button>
                  )}
                  <button onClick={() => handleDeletar(action.id, action.title)} disabled={salvando}
                    className="px-3 py-2 rounded-lg border-2 border-gray-300 text-red-600 hover:bg-red-50 transition-all disabled:opacity-50"
                    title="Deletar">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Empty state */}
      {!loading && !error && actionsFiltradas.length === 0 && (
        <div className="bg-white rounded-2xl border border-gray-200 p-12 text-center">
          <Heart className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-600 font-semibold text-lg mb-2">Nenhuma ação social encontrada</p>
          <p className="text-gray-400 text-sm mb-6">Crie a primeira iniciativa social da Federação</p>
          <button onClick={() => { setFormCriar(EMPTY_FORM); setModalCriar(true); }}
            className="px-6 py-3 bg-emerald-600 text-white font-bold rounded-xl hover:bg-emerald-700 transition-all inline-flex items-center gap-2">
            <Plus className="w-5 h-5" /> Nova Ação Social
          </button>
        </div>
      )}

      {/* ── MODAL CRIAR ───────────────────────────────────────────────────── */}
      {modalCriar && (
        <FormModal
          title="Nova Ação Social"
          form={formCriar}
          onChange={setFormCriar}
          onSave={handleCriar}
          onClose={() => setModalCriar(false)}
          saving={salvando}
          saveLabel="Publicar Agora"
        />
      )}

      {/* ── MODAL EDITAR ──────────────────────────────────────────────────── */}
      {modalEditar && (
        <FormModal
          title="Editar Ação Social"
          form={formEditar}
          onChange={setFormEditar}
          onSave={handleEditar}
          onClose={() => { setModalEditar(false); setEditandoId(null); }}
          saving={salvando}
          saveLabel="Salvar Alterações"
        />
      )}

      {/* ── MODAL DETALHES ────────────────────────────────────────────────── */}
      {detalhes && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-auto shadow-2xl">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white z-10">
              <h3 className="text-xl font-black text-gray-900">Detalhes da Ação Social</h3>
              <button onClick={() => setDetalhes(null)} className="text-gray-400 hover:text-gray-600">
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              {detalhes.coverImageUrl && (
                <img src={detalhes.coverImageUrl} alt={detalhes.title} className="w-full h-56 object-cover rounded-xl" />
              )}
              <div className="flex gap-2 flex-wrap">
                <StatusBadge status={detalhes.status} />
              </div>
              <h4 className="text-2xl font-black text-gray-900">{detalhes.title}</h4>
              {detalhes.summary && (
                <p className="text-gray-600 border-l-4 border-emerald-500 pl-4 italic">{detalhes.summary}</p>
              )}

              {/* Info grid */}
              <div className="grid grid-cols-2 gap-4 py-3 border-y border-gray-100">
                {detalhes.eventDate && (
                  <div className="flex items-center gap-2 text-sm text-gray-700">
                    <Calendar className="w-4 h-4 text-emerald-500" />
                    <span className="font-semibold">{fmtDate(detalhes.eventDate)}</span>
                  </div>
                )}
                {detalhes.location && (
                  <div className="flex items-center gap-2 text-sm text-gray-700">
                    <MapPin className="w-4 h-4 text-emerald-500" />
                    <span className="font-semibold">{detalhes.location}</span>
                  </div>
                )}
                <div className="flex items-center gap-2 text-sm text-gray-500">
                  <Eye className="w-4 h-4" />
                  <span>{detalhes.viewsCount} visualizações</span>
                </div>
                {detalhes.publishedAt && (
                  <div className="flex items-center gap-2 text-sm text-gray-500">
                    <Calendar className="w-4 h-4" />
                    <span>Publicado em {fmtDate(detalhes.publishedAt)}</span>
                  </div>
                )}
              </div>

              {/* Descrição */}
              <div>
                <h5 className="text-sm font-bold text-gray-700 mb-2">Descrição completa:</h5>
                <div className="text-gray-700 leading-relaxed prose max-w-none"
                  dangerouslySetInnerHTML={{ __html: detalhes.description }} />
              </div>

              {/* Parceiros */}
              {detalhes.partners.length > 0 && (
                <div>
                  <h5 className="text-sm font-bold text-gray-700 mb-3 flex items-center gap-2">
                    <Users className="w-4 h-4 text-emerald-600" />
                    Parceiros / Patrocinadores
                  </h5>
                  <div className="flex flex-wrap gap-3">
                    {detalhes.partners.map((p, i) => (
                      <div key={i} className="flex items-center gap-2 bg-gray-50 rounded-xl px-3 py-2 border border-gray-200">
                        {p.logoUrl && <img src={p.logoUrl} alt={p.name} className="w-7 h-7 object-contain rounded" />}
                        <div>
                          <p className="text-sm font-bold text-gray-900">{p.name}</p>
                          {p.website && (
                            <a href={p.website} target="_blank" rel="noopener noreferrer"
                              className="text-xs text-blue-500 hover:underline flex items-center gap-1">
                              <Globe className="w-3 h-3" /> Site
                            </a>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="p-6 border-t border-gray-200 flex gap-3 sticky bottom-0 bg-white">
              <button onClick={() => setDetalhes(null)}
                className="flex-1 px-4 py-3 rounded-xl border-2 border-gray-300 text-gray-700 font-bold hover:bg-gray-50">
                Fechar
              </button>
              <button onClick={() => { abrirEditar(detalhes); setDetalhes(null); }}
                className="px-4 py-3 rounded-xl border-2 border-amber-300 text-amber-700 font-bold hover:bg-amber-50 flex items-center gap-2">
                <Edit className="w-4 h-4" /> Editar
              </button>
              {detalhes.status !== 'published' && (
                <button onClick={() => handlePublicar(detalhes.id)} disabled={salvando}
                  className="flex-1 px-4 py-3 rounded-xl bg-green-600 text-white font-bold hover:bg-green-700 flex items-center justify-center gap-2 disabled:opacity-60">
                  {salvando ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />} Publicar
                </button>
              )}
              {detalhes.status === 'published' && (
                <button onClick={() => handleArquivar(detalhes.id)} disabled={salvando}
                  className="flex-1 px-4 py-3 rounded-xl bg-amber-500 text-white font-bold hover:bg-amber-600 flex items-center justify-center gap-2 disabled:opacity-60">
                  {salvando ? <Loader2 className="w-5 h-5 animate-spin" /> : <Archive className="w-5 h-5" />} Arquivar
                </button>
              )}
              <button onClick={() => handleDeletar(detalhes.id, detalhes.title)} disabled={salvando}
                className="px-4 py-3 rounded-xl border-2 border-red-200 text-red-600 hover:bg-red-50 font-bold disabled:opacity-60">
                <Trash2 className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GerenciamentoAcoesSociais;